import { EventActionType } from '@sdk/shifts/types/ActivityLogType';
import type { ActivityLogItemType } from '@sdk/shifts/types/ActivityLogType';
import gettext, { ngettext } from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { EVENT_STATUSES } from '@sdk/schedule/constants';

export const getActivityLogItemSummary = (item: ActivityLogItemType): [string, ThemeColorType] => {
  const fieldsChanged = Object.keys(item.data || {});
  const fieldsChangedRegardlessOfStatus = fieldsChanged.filter((field) => field !== 'status');

  switch (item.type) {
    case EventActionType.Created:
      if (item.items.every((shift) => shift.rrule)) {
        return [
          ngettext(
            'Created a recurring shift',
            'Created %(shifts)s recurring shifts',
            item.items.length,
            {
              shifts: item.items.length,
            }
          ),
          ThemeColorType.primary,
        ];
      }

      return [
        ngettext('Created a shift', 'Created %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.primary,
      ];
    case EventActionType.CreatedAndPublished:
      return [
        ngettext('Published a shift', 'Published %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.success,
      ];
    case EventActionType.CreatedViaClockin:
      return [gettext('Created a shift by clocking in'), ThemeColorType.primary];
    case EventActionType.CreatedViaTimesheetEdit:
      return [
        ngettext(
          'Created a shift by editing timesheets',
          'Created %(shifts)s shifts by editing timesheets',
          item.items.length,
          {
            shifts: item.items.length,
          }
        ),
        ThemeColorType.primary,
      ];
    case EventActionType.CreatedViaImport:
      return [
        ngettext('Imported a shift', 'Imported %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.primary,
      ];
    case EventActionType.Changed:
      if (
        fieldsChanged.length === 1 &&
        fieldsChanged[0] === 'status' &&
        item.data.status.from === EVENT_STATUSES.PUBLISHED
      ) {
        return [
          ngettext('Unpublished a shift', 'Unpublished %(shifts)s shifts', item.items.length, {
            shifts: item.items.length,
          }),
          ThemeColorType.primary,
        ];
      }

      if (
        fieldsChangedRegardlessOfStatus.length === 1 &&
        fieldsChangedRegardlessOfStatus[0] === 'user'
      ) {
        return [
          ngettext('Reassigned a shift', 'Reassigned %(shifts)s shifts', item.items.length, {
            shifts: item.items.length,
          }),
          ThemeColorType.primary,
        ];
      }

      return [
        ngettext('Edited a shift', 'Edited %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.primary,
      ];
    case EventActionType.Published:
      return [
        ngettext('Published a shift', 'Published %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.success,
      ];
    case EventActionType.Unpublished:
      return [
        ngettext('Unpublished a shift', 'Unpublished %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.primary,
      ];
    case EventActionType.ChangeCancelled:
      return [
        ngettext(
          'Undid changes to a shift',
          'Undid changes to %(shifts)s shifts',
          item.items.length,
          {
            shifts: item.items.length,
          }
        ),
        ThemeColorType.gray,
      ];
    case EventActionType.Cancelled: // Deleted directly by a user
      return [
        ngettext('Deleted a shift', 'Deleted %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.danger,
      ];
    case EventActionType.CancelledViaLocation: // Deleted as a result of location archive/delete
      return [
        ngettext('Deleted a shift', 'Deleted %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.danger,
      ];
    case EventActionType.CancelledViaPosition: // Deleted as a result of position archive/delete
      return [
        ngettext('Deleted a shift', 'Deleted %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.danger,
      ];
    case EventActionType.CancelledViaUser: // Deleted as a result of assigned user being deleted/deactivated
      return [
        ngettext('Deleted a shift', 'Deleted %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.danger,
      ];
    case EventActionType.CancelledViaUserDeactivate: // Deleted as a result of assigned user being deactivated
      return [
        ngettext('Deleted a shift', 'Deleted %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.danger,
      ];
    case EventActionType.CancelledViaUserDelete: // Deleted as a result of assigned user being deleted
      return [
        ngettext('Deleted a shift', 'Deleted %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.danger,
      ];
    case EventActionType.MadeAvailable:
      return [
        ngettext('Made a shift available', 'Made %(shifts)s shifts available', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.primary,
      ];
    case EventActionType.MadeSwappable:
      return [gettext('Offered a shift'), ThemeColorType.primary];
    case EventActionType.AvailabilityCancelled:
      return [
        ngettext(
          "Canceled shift's availability",
          "Canceled %(shifts)s shift's availability",
          item.items.length,
          {
            shifts: item.items.length,
          }
        ),
        ThemeColorType.success,
      ];
    case EventActionType.ReceivedApplication:
      return [gettext('Applied for a shift'), ThemeColorType.primary];
    case EventActionType.ApplicationRetracted:
      return [gettext("Retracted shift's aplication"), ThemeColorType.danger];
    case EventActionType.ApplicationApproved:
      return [gettext('Approved a shift aplication'), ThemeColorType.success];
    case EventActionType.ApplicationDenied:
      return [gettext('Denied a shift aplication'), ThemeColorType.danger];
    case EventActionType.SwapAccepted:
      return [gettext('Accepted a shift offer'), ThemeColorType.success];
    case EventActionType.SwapApproved:
      return [gettext('Approved a shift offer'), ThemeColorType.success];
    case EventActionType.SwapCancelled:
      return [gettext('Canceled a shift offer'), ThemeColorType.danger];
    case EventActionType.SwapDenied:
      return [gettext('Denied a shift offer'), ThemeColorType.danger];
    case EventActionType.SwapRejected:
      return [gettext('Rejected a shift offer'), ThemeColorType.danger];
    case EventActionType.DoubleExchangeCreated:
      return [gettext('Created a double shift exchange'), ThemeColorType.primary];
    case EventActionType.DoubleExchangeAccepted:
      return [gettext('Accepted a double shift exchange'), ThemeColorType.success];
    case EventActionType.DoubleExchangeApproved:
      return [gettext('Approved a double shift exchange'), ThemeColorType.success];
    case EventActionType.DoubleExchangeCancelled:
      return [gettext('Canceled a double shift exchange'), ThemeColorType.danger];
    case EventActionType.DoubleExchangeDenied:
      return [gettext('Denied a double shift exchange'), ThemeColorType.danger];
    case EventActionType.DoubleExchangeRejected:
      return [gettext('Rejected a double shift exchange'), ThemeColorType.danger];
    case EventActionType.ExchangePerformed:
      return [gettext('Performed a shift exchange'), ThemeColorType.success];
    case EventActionType.TransactionReverted:
      return [gettext('Undid changes to the schedule'), ThemeColorType.gray];
    case EventActionType.LeaveRequested:
      return [gettext('Requested a time off'), ThemeColorType.primary];
    case EventActionType.LeaveChanged:
      return [gettext('Change a time off request'), ThemeColorType.primary];
    case EventActionType.LeaveApproved:
      return [gettext('Approved a time off request'), ThemeColorType.success];
    case EventActionType.LeaveDenied:
      return [gettext('Denied a time off request'), ThemeColorType.danger];
    case EventActionType.LeaveCancelled:
      return [gettext('Canceled a time off request'), ThemeColorType.gray];
    case EventActionType.Accepted:
      return [
        ngettext('Accepted a shift', 'Accepted %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.success,
      ];
    case EventActionType.Rejected:
      return [
        ngettext('Rejected a shift', 'Rejected %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.danger,
      ];
    case EventActionType.AssigneeNotesUpdated:
      return [gettext('Updated shift notes'), ThemeColorType.gray];
    case EventActionType.AutoAssigned:
      return [
        ngettext('Auto-assigned a shift', 'Auto-assigned %(shifts)s shifts', item.items.length, {
          shifts: item.items.length,
        }),
        ThemeColorType.primary,
      ];
    case EventActionType.CopiedAndAssigned:
      return [
        ngettext(
          'Copied and auto-assigned a shift',
          'Copied and auto-assigned %(shifts)s shifts',
          item.items.length,
          {
            shifts: item.items.length,
          }
        ),
        ThemeColorType.primary,
      ];
    default:
      return [gettext('Unknown action'), ThemeColorType.gray];
  }
};
